import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { AliveState } from '../../core/ActiveState';
import { ZalamoDialogService } from '../zalamo-dialog/zalamo-dialog.service';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { reorderMenu } from './navigation-order';
import { FullscreenRef } from '../zalamo-dialog/fullscreen-ref';
import { UploadService } from '../../upload/upload.service';
import { CurrentQueueViewComponent } from '../../upload/views/current-queue-view/current-queue-view.component';
import { QuickPreviewService } from '../smart-preview/quick-preview.service';
import { TooltipRef } from '../zalamo-tooltip/tooltip-ref';
import { JWTService } from '../../zalamo/auth/jwt-parser-service';
import { select, Store } from '@ngrx/store';
import {
  selectHasMobileMenuDisabled,
  selectIsNavigationOpen,
  selectNavigationHasSave,
  selectNavigationHasSubmenu,
} from './navigation-state/navigation-state.selectors';
import {
  navigationMobileSubmenuClose,
  navigationMobileSubmenuToggleOpenState,
} from './navigation-state/navigation-state.actions';
import { NavigationStart, Router } from '@angular/router';
import { UnseenCommentsListComponent } from 'ZalamoLibrary/src/lib/ui/comments/unseen-comments-icon/unseen-comments-list/unseen-comments-list.component';
import { MenuMotion, naturalMenuWidth } from './menu-motion';
import { OrganizationService } from 'ZalamoLibrary/src/lib/core/organizations/organization.service';

const VERTICAL_ITEM_HEIGHT = 56;

export interface NavigationMenuItem {
  path: Array<string>;
  label: string;
  icon: string;
  badge?: number;
  isDefault: boolean;
  isVisible: boolean;
  id: string;
  isMatIcon?: boolean;
  mobileOnly?: boolean;
}

export interface BottomMenuItem {
  label: string;
  url: Array<string>;
}

export interface ElementAvailableToAdd<T> {
  badge: string;
  icon: string;
  id: string;
  parent: T;
  isMain: boolean;
  isMatIcon?: boolean;

  handler(): any;
}

@Component({
  selector: 'lib-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class NavigationComponent extends AliveState implements OnInit {
  @ViewChild('motionMenu') motionMenu?: ElementRef<HTMLElement>;
  private readonly menuMotion = new MenuMotion();
  private motionKey = '';
  private motionElement?: HTMLElement;

  ngAfterViewChecked(): void {
    const element = this.motionMenu?.nativeElement;
    if (!element) return;
    const key = [this.isMobile, this.isMobileOpenState, this.LeftAnimation].join(':');
    if (this.motionKey === key && this.motionElement === element) return;
    this.motionKey = key;
    this.motionElement = element;
    if (this.isMobile) {
      const open = this.isMobileOpenState === 'open';
      this.menuMotion.play(element, { width: '100vw', top: open ? '0px' : '100vh',
        left: open ? '0px' : '-33vw', transform: open ? 'scale(1)' : 'scale(.3)' }, 100);
    } else {
      element.style.top = '';
      element.style.transform = '';
      element.style.left = '';
      this.menuMotion.play(element, { width: this.isHorizontal ? '100%' : this.isOpen ? naturalMenuWidth(element) : '60px' }, 100);
    }
  }

  override ngOnDestroy(): void { this.menuMotion.destroy(); super.ngOnDestroy(); }

  visibleMenu$ = new BehaviorSubject([]);
  hiddenMenu$ = new BehaviorSubject([]);
  @Input() userType: 'photographer' | 'retouch' | 'retoucher' = 'photographer';
  @Input() isOpen = false;
  @Input() hasSubMenu = false;
  @Input() availableToAdd: Array<ElementAvailableToAdd<any>> = [];
  @Output() changeVisibility = new EventEmitter<{
    id: string;
    visibility: boolean;
  }>();
  @Input() hasZalamoPrint = false;

  @Input() hasUpload = false;
  @Output() logout = new EventEmitter();
  @Input() appTasksInProgress = 0;
  @Input() hasApp = false;
  @Input() uploadDone = 0;
  @Input() uploadInProgress = 0;
  @Input() disableAllAps = false;
  @ViewChild('links', { static: true }) links: ElementRef<HTMLElement>;
  @ViewChild('close', { static: false }) close;
  @ViewChild('allApps', { static: false }) allApps;
  @ViewChild('addPanel', { static: false }) addPanel;
  @Output() showMobile = new EventEmitter();
  @Output() showAppTasks = new EventEmitter();
  @Input() customLogo = null;
  shouldCalculateHorizontalSize$ = new Subject();
  calcHorizontal$ = new Subject();
  wholeMenu: Array<NavigationMenuItem> = [];
  @Output() sortUpdated = new EventEmitter<Array<string>>();
  blockOpenOnHover = false;
  quickPreviewInit$ = new Subject<{ type: string; target: EventTarget }>();
  lastTooltipRef: TooltipRef<any> = null;
  @HostBinding('class.notCalculated') firstShow = true;
  showAddHelp = false;
  allRef: FullscreenRef<any> = null;
  whereIsMouse = null;
  _previewOpen = false;
  @Input() hasAdd = true;
  public previewEntered = false;
  readonly sortingDisabled = this.breakpointObserver.isMatched(
    Breakpoints.Handset,
  );
  sortMenu(event: CdkDragDrop<NavigationMenuItem[]>): void {
    const menu = reorderMenu(
      this.wholeMenu,
      event.previousIndex,
      event.currentIndex,
    );
    this.wholeMenu = menu;
    this.sortUpdated.emit(menu.map(({ id }) => id));
  }
  @Input() showCommentsIcon = false;
  @Input() bottomMenu: Array<BottomMenuItem> = [];
  addRef: FullscreenRef<any>;
  hasSubMenu$ = this.store.pipe(select(selectNavigationHasSubmenu));
  mobileMenuDisabled$ = this.store.pipe(select(selectHasMobileMenuDisabled));
  isMobile$ = combineLatest([
    this.breakpointObserver
      .observe(Breakpoints.Handset)
      .pipe(map(({ matches }) => matches)),
    this.mobileMenuDisabled$,
  ]).pipe(
    map(
      ([breakpointActive, menuDisabled]) => breakpointActive && !menuDisabled,
    ),
  );
  lockMouseOver = false;
  sortOrder$ = new BehaviorSubject([]);
  sortedMenu$ = combineLatest([this.visibleMenu$, this.sortOrder$]).pipe(
    map(([visibleMenu, sortOrder]) =>
      visibleMenu.sort((a, b) =>
        sortOrder.indexOf(a.id) > sortOrder.indexOf(b.id) ? 1 : -1,
      ),
    ),
  );
  unsaved$ = new BehaviorSubject(true);
  @Input() menuConfigNotLoaded = false;
  hasSave$ = this.store.pipe(select(selectNavigationHasSave));
  organizationsInPlay$ = new BehaviorSubject<
    { name: string; organizationId: string }[]
  >([]);
  currentOrganization$ = combineLatest([
    this.organizationService.getCurrentOrganization(),
    this.organizationsInPlay$.pipe(
      filter((v) => Array.isArray(v) && v.length > 0),
    ),
  ]).pipe(
    map(([currentOrganizationId, allOrganizations]) => {
      return allOrganizations.find(
        ({ organizationId }) => organizationId === currentOrganizationId,
      );
    }),
  );
  private _menu: Array<NavigationMenuItem> = [];
  private menuSizes = [];
  private isMobile = false;
  private mouseOverMenu = false;

  @Input() set organizations(
    organizations: Array<{ name: string; organizationId: string }>,
  ) {
    this.organizationsInPlay$.next(organizations);
  }

  @Input() set shouldOpenAdd(open) {
    if (open === true) {
      this.openAdd();
    }
  }

  get availableForAddTopPriority() {
    return this.availableToAdd.filter(({ isMain }) => isMain);
  }

  get availableForAddLowPriority() {
    return this.availableToAdd.filter(({ isMain }) => !isMain);
  }

  _isMobileOpen = null;

  get isMobileOpen() {
    return this._isMobileOpen;
  }

  @Input() set isMobileOpen(isMobileOpen) {
    if (isMobileOpen) {
      this.store.dispatch(navigationMobileSubmenuClose());
    }
    this._isMobileOpen = isMobileOpen;
  }

  get isPreviewOpen() {
    return this._previewOpen;
  }

  set isPreviewOpen(open) {
    this._previewOpen = open;
    if (!open) {
      setTimeout(() => {
        if (!this.mouseOverMenu) {
          this.isOpen = false;
        }
      }, 1000);
    }
  }

  _quickPreviewLeft = null;

  get quickPreviewLeft() {
    return this._quickPreviewLeft;
  }

  set quickPreviewLeft(val) {
    console.log('SET LAST LEFT TO', val);
    this.whereIsMouse = null;
    if (this.lastTooltipRef) {
      setTimeout(() => {
        if (!this.previewEntered && this.lastTooltipRef) {
          this.lastTooltipRef.close();
        }
      }, 50);
    }

    this._quickPreviewLeft = val;
  }

  @Input() set showAddPanel(x) {
    setTimeout(() => this.openAdd(), 0);
  }

  private _isHorizontal = false;

  get isHorizontal() {
    return this._isHorizontal;
  }

  @Input() @HostBinding('class.horizontal') set isHorizontal(isHorizontal) {
    this._isHorizontal = isHorizontal;
    this.menuSizes = [];
  }

  @Input() set sortOrder(order: Array<string>) {
    console.log('SORT ORDER', order);
    this.sortOrder$.next(order);
    this.wholeMenu = this.wholeMenu.sort((a, b) =>
      this.sortOrder$.value.indexOf(a.id) > this.sortOrder$.value.indexOf(b.id)
        ? 1
        : -1,
    );
  }

  @Input() set mainMenu(menu: Array<NavigationMenuItem>) {
    console.log('NEW MENU SET', menu);
    this.menuSizes = [];
    this._menu = menu.filter(
      ({ isDefault, isVisible, mobileOnly }) =>
        (isVisible || isDefault) &&
        (!mobileOnly ||
          this.breakpointObserver.isMatched([Breakpoints.Handset])),
    );
    this.wholeMenu = menu
      .filter(
        ({ isDefault, mobileOnly }) =>
          !isDefault &&
          (!mobileOnly ||
            this.breakpointObserver.isMatched([Breakpoints.Handset])),
      )
      .sort((a, b) =>
        this.sortOrder$.value.indexOf(a.id) >
        this.sortOrder$.value.indexOf(b.id)
          ? 1
          : -1,
      );
    this.visibleMenu$.next(this._menu);
  }

  get LeftAnimation() {
    if (this.isHorizontal) {
      return null;
    }
    return this.isOpen ? 'open' : 'closed';
  }

  get isMobileOpenState() {
    switch (this.isMobileOpen) {
      case true:
        return 'open';
      case false:
        return 'closed';
      case null:
        return null;
    }
  }

  constructor(
    private breakpointObserver: BreakpointObserver,
    private zalamoDialog: ZalamoDialogService,
    public uploadService: UploadService,
    private previewService: QuickPreviewService,
    private _viewContainerRef: ViewContainerRef,
    public jwtService: JWTService,
    private store: Store,
    private router: Router,
    public organizationService: OrganizationService,
  ) {
    super();

    this.subscribeWhileAlive(
      this.currentOrganization$.pipe(tap((x) => console.log(x))),
      combineLatest([
        this.shouldCalculateHorizontalSize$.pipe(startWith(0)),
        this.ngOnChanges$,
      ]).pipe(
        debounceTime(100),
        tap(() => this.calcHorizontal()),
      ),
      this.router.events.pipe(
        filter((event) => this.isMobile && event instanceof NavigationStart),
        tap(() => (this.isMobileOpen = false)),
      ),
      this.store.pipe(
        select(selectIsNavigationOpen),
        tap((isOpen) => (this.isOpen = isOpen)),
      ),
    );
  }

  @HostListener('window:resize') onResize() {
    this.calcHorizontal();
  }

  ngOnInit() {
    this.subscribeWhileAlive(
      this.isMobile$.pipe(
        map((res) => (this.isMobile = res)),
        distinctUntilChanged(),
        tap((isMobile) => {
          this.visibleMenu$.next(this.visibleMenu$.value.reverse());
        }),
      ),
      this.showMobile.pipe(
        tap(() => (this.isMobileOpen = false)),
        tap(() =>
          this.store.dispatch(navigationMobileSubmenuToggleOpenState()),
        ),
      ),
    );
  }

  calcVertical() {
    if (this.isHorizontal) {
      return false;
    }
    const hostSize = this.links.nativeElement.getBoundingClientRect();

    const sizes = this.getMenuSizes();
    let start = 0;
    const i = sizes.findIndex(([width, size]) => {
      const res = size + start;
      start += size;
      return res > hostSize.height - 140;
    });
    if (i > 0) {
      this.visibleMenu$.next([...this._menu].splice(0, i - 1));
      this.hiddenMenu$.next(this._menu.slice(i));
    } else {
      this.visibleMenu$.next([...this._menu]);
    }

    this.handleFirstShow();
  }

  handleFirstShow() {
    if (this.firstShow) {
      setTimeout(() => {
        this.firstShow = false;
      }, 1000);
    }
  }

  getMenuSizes() {
    const sizes = this.menuSizes;
    if (this.menuSizes.length === 0) {
      this.visibleMenu$.next(this._menu);
      this.links.nativeElement.querySelectorAll('a').forEach((el) => {
        const { width, height } = el.getBoundingClientRect();
        sizes.push([width, height]);
      });
    }
    return sizes;
  }

  calcHorizontal() {
    if (!this.isHorizontal) {
      this.calcVertical();
      return;
    }
    const hostSize = this.links.nativeElement.getBoundingClientRect();

    const sizes = this.getMenuSizes();
    let start = 0;
    const i = sizes.findIndex(([size]) => {
      const res = size + start;
      start += size;
      return res > hostSize.width - 60;
    });
    if (i > 0) {
      this.visibleMenu$.next([...this._menu].splice(0, i - 1));
      this.hiddenMenu$.next(this._menu.slice(i));
    } else {
      this.visibleMenu$.next([...this._menu]);
    }
    this.handleFirstShow();
  }

  openAll() {
    this.blockOpenOnHover = true;
    this.isOpen = false;
    this.allRef = this.zalamoDialog.openFull(this.allApps, {
      keepHorizontalMenuVisible: !this.isMobile && this.isHorizontal,
      keepVerticalMenuVisible: !this.isMobile && !this.isHorizontal,
      keepMobileBottomMenuVisible: this.isMobile,
      // isBare: true,
    });
    this.allRef
      .afterClosed()
      .toPromise()
      .then(() => {
        this.blockOpenOnHover = false;
      });
    this.router.events
      .pipe(takeUntil(this.allRef.beforeClosed()))
      .subscribe(() => this.allRef.close());
  }

  hideAll() {
    if (this.allRef) {
      this.allRef.close();
    }
  }

  openAdd() {
    this.blockOpenOnHover = true;
    this.isOpen = false;
    this.addRef = this.zalamoDialog.openFull(this.addPanel);
    this.addRef
      .afterClosed()
      .toPromise()
      .then(() => {
        this.blockOpenOnHover = false;
      });
  }

  closeAddWindow() {
    this.addRef.close();
  }

  mouseEnter() {
    if (this.lockMouseOver) {
      return;
    }
    if (!this.isMobile && !this.blockOpenOnHover) {
      this.isOpen = true;
    }
    this.mouseOverMenu = true;
  }

  navClicked() {
    console.log('MENU LOCKED' + '');
    this.lockMouseOver = true;
    this.isOpen = false;
  }

  mouseLeave() {
    this.lockMouseOver = false;
    if (!this.isMobile && !this.blockOpenOnHover && !this.isPreviewOpen) {
      this.isOpen = false;
    }
    this.mouseOverMenu = false;
  }

  trackMenuItem(i, v) {
    return v.id;
  }

  openUploadQueue() {
    this.quickPreviewLeft = null;
    if (this.lastTooltipRef) {
      this.lastTooltipRef.close();
      this.quickPreviewLeft = null;
    }
    const ref = this.zalamoDialog.openFull(CurrentQueueViewComponent, {
      transformOrigin: `30px ${innerHeight - 100}px`,
    });
    ref.componentInstance.emptyAfterCancel
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe(() => ref.close());
  }

  openUnseenComments() {
    this.quickPreviewLeft = null;
    if (this.lastTooltipRef) {
      this.lastTooltipRef.close();
      this.quickPreviewLeft = null;
    }
    const ref = this.zalamoDialog.openFull(UnseenCommentsListComponent, {
      transformOrigin: `30px ${innerHeight - 100}px`,
    });
    ref.componentInstance.emptyAfterCancel
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe(() => ref.close());
  }
}
